'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, Filter, Download, Ban, Trash2, CheckCircle, ChevronLeft, ChevronRight, Star, MapPin, Briefcase, Users, Loader2, Tag, X, Plus } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

type TabType = 'artisans' | 'customers';

interface UserRole {
  id: string;
  roleName: string;
  role_id?: number | null;
}

// Unified row type merging user_profile + artisan_profile or customer_profile
interface UserRow {
  // from user_profile
  id: string; // user_profile.id
  user_id: string; // auth.users.id
  first_name: string;
  last_name: string;
  display_name: string;
  full_name: string; // computed: first_name + last_name
  email: string; // from user_profiles (legacy) or joined
  city: string;
  country: string;
  verification_status: string;
  is_active: boolean;
  is_suspended: boolean;
  created_at: string;
  // from artisan_profile
  artisan_code?: string;
  specialization?: string;
  business_name?: string;
  average_rating?: number;
  completed_jobs?: number;
  hourly_rate?: number;
  artisan_status?: string;
  is_available?: boolean;
  verification_badge?: boolean;
  // from customer_profile
  customer_code?: string;
  total_spent?: number;
  total_bookings?: number;
  completed_bookings?: number;
  cancelled_bookings?: number;
  vip_status?: boolean;
  customer_status?: string;
  // legacy fallback fields from user_profiles
  trust_score?: number;
  jobs_completed?: number;
  jobs_posted?: number;
  total_revenue?: number;
  dispute_count?: number;
  specialty?: string;
  status?: string;
  last_active_at?: string;
  userRoles?: UserRole[];
}

const FRONTEND_ROLES = ['artisan', 'customer', 'religious_organization', 'agent'];
const ADMIN_ROLES = ['super_admin', 'finance', 'operations', 'support_admin', 'finance_admin'];

function TrustStars({ score }: { score: number }) {
  return (
    <div className="flex items-center gap-1">
      <Star
        size={12}
        className={
          score >= 4.5
            ? 'text-amber-400 fill-amber-400'
            : score >= 3
            ? 'text-amber-300 fill-amber-300' :'text-red-400 fill-red-400'
        }
      />
      <span className="text-xs font-600 font-tabular text-foreground">{score?.toFixed(1)}</span>
    </div>
  );
}

function BadgeCheckIcon({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return 'Unknown';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB');
}

function RoleBadges({ roles }: { roles?: UserRole[] }) {
  if (!roles || roles.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <span
          key={r.id}
          className="text-xs px-1.5 py-0.5 rounded-full bg-accent/10 text-accent font-500"
        >
          {r.roleName.replace(/_/g, ' ')}
        </span>
      ))}
    </div>
  );
}

export default function UserManagementContent() {
  const [activeTab, setActiveTab] = useState<TabType>('artisans');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [artisans, setArtisans] = useState<UserRow[]>([]);
  const [customers, setCustomers] = useState<UserRow[]>([]);
  const [roleCounts, setRoleCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: string;
    name: string;
    id: string;
    table: 'user_profiles' | 'user_profile';
  }>({ open: false, action: '', name: '', id: '', table: 'user_profiles' });
  const [roleModal, setRoleModal] = useState<{
    open: boolean;
    user: UserRow | null;
  }>({ open: false, user: null });
  const [roleActionLoading, setRoleActionLoading] = useState(false);
  const [newRole, setNewRole] = useState('');
  const perPage = 8;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();

    // Strategy: Try new schema (user_profile + artisan_profile/customer_profile) first,
    // fall back to legacy user_profiles if new tables are empty.
    const [
      newArtisanRes,
      newCustomerRes,
      legacyArtisanRes,
      legacyCustomerRes,
      userRolesCountRes,
    ] = await Promise.all([
      // New schema: user_profile joined with artisan_profile
      supabase
        .from('user_profile')
        .select(`
          id,
          user_id,
          first_name,
          middle_name,
          last_name,
          display_name,
          city,
          country,
          verification_status,
          is_active,
          is_suspended,
          created_at,
          artisan_profile (
            id,
            artisan_code,
            specialization,
            business_name,
            average_rating,
            completed_jobs,
            hourly_rate,
            status,
            is_available,
            verification_badge
          )
        `)
        .not('artisan_profile', 'is', null)
        .order('created_at', { ascending: false }),

      // New schema: user_profile joined with customer_profile
      supabase
        .from('user_profile')
        .select(`
          id,
          user_id,
          first_name,
          middle_name,
          last_name,
          display_name,
          city,
          country,
          verification_status,
          is_active,
          is_suspended,
          created_at,
          customer_profile (
            id,
            customer_code,
            total_spent,
            total_bookings,
            completed_bookings,
            cancelled_bookings,
            vip_status,
            status
          )
        `)
        .not('customer_profile', 'is', null)
        .order('created_at', { ascending: false }),

      // Legacy fallback: user_profiles with role = 'artisan'
      supabase
        .from('user_profiles')
        .select('*')
        .eq('role', 'artisan')
        .order('created_at', { ascending: false }),

      // Legacy fallback: user_profiles with role = 'customer'
      supabase
        .from('user_profiles')
        .select('*')
        .eq('role', 'customer')
        .order('created_at', { ascending: false }),

      // Role counts
      supabase
        .from('user_roles')
        .select('role_id, roles!user_roles_role_id_fkey(name)'),
    ]);

    // Count users per role
    const counts: Record<string, number> = {};
    (userRolesCountRes.data ?? []).forEach((r: any) => {
      const roleName: string | undefined = r.roles?.name ?? undefined;
      if (roleName) {
        counts[roleName] = (counts[roleName] ?? 0) + 1;
      }
    });
    setRoleCounts(counts);

    // Determine which data source to use
    const newArtisans = newArtisanRes.data ?? [];
    const newCustomers = newCustomerRes.data ?? [];
    const legacyArtisans = legacyArtisanRes.data ?? [];
    const legacyCustomers = legacyCustomerRes.data ?? [];

    let artisanProfiles: UserRow[] = [];
    let customerProfiles: UserRow[] = [];

    if (newArtisans.length > 0) {
      // Use new schema data
      artisanProfiles = newArtisans.map((u: any) => {
        const ap = Array.isArray(u.artisan_profile) ? u.artisan_profile[0] : u.artisan_profile;
        const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.display_name || 'Unknown';
        return {
          id: u.id,
          user_id: u.user_id,
          first_name: u.first_name ?? '',
          last_name: u.last_name ?? '',
          display_name: u.display_name ?? '',
          full_name: fullName,
          email: '',
          city: u.city ?? '',
          country: u.country ?? '',
          verification_status: u.verification_status ?? 'unverified',
          is_active: u.is_active ?? true,
          is_suspended: u.is_suspended ?? false,
          created_at: u.created_at,
          artisan_code: ap?.artisan_code ?? '',
          specialization: ap?.specialization ?? '',
          business_name: ap?.business_name ?? '',
          average_rating: ap?.average_rating ?? 0,
          completed_jobs: ap?.completed_jobs ?? 0,
          hourly_rate: ap?.hourly_rate ?? 0,
          artisan_status: ap?.status ?? 'active',
          is_available: ap?.is_available ?? true,
          verification_badge: ap?.verification_badge ?? false,
          // legacy compat
          trust_score: ap?.average_rating ?? 0,
          jobs_completed: ap?.completed_jobs ?? 0,
          specialty: ap?.specialization ?? '',
          status: ap?.status ?? (u.is_active ? 'active' : u.is_suspended ? 'suspended' : 'pending'),
          userRoles: [],
        };
      });
    } else {
      // Fall back to legacy user_profiles
      artisanProfiles = legacyArtisans.map((u: any) => ({
        id: u.id,
        user_id: u.id,
        first_name: u.full_name?.split(' ')[0] ?? '',
        last_name: u.full_name?.split(' ').slice(1).join(' ') ?? '',
        display_name: u.full_name ?? '',
        full_name: u.full_name ?? '',
        email: u.email ?? '',
        city: u.city ?? '',
        country: '',
        verification_status: u.verification_status ?? 'unverified',
        is_active: u.status === 'active',
        is_suspended: u.status === 'suspended',
        created_at: u.created_at,
        specialization: u.specialty ?? '',
        specialty: u.specialty ?? '',
        average_rating: u.trust_score ?? 0,
        trust_score: u.trust_score ?? 0,
        completed_jobs: u.jobs_completed ?? 0,
        jobs_completed: u.jobs_completed ?? 0,
        total_revenue: u.total_revenue ?? 0,
        dispute_count: u.dispute_count ?? 0,
        status: u.status ?? 'active',
        last_active_at: u.last_active_at ?? '',
        userRoles: [],
      }));
    }

    if (newCustomers.length > 0) {
      customerProfiles = newCustomers.map((u: any) => {
        const cp = Array.isArray(u.customer_profile) ? u.customer_profile[0] : u.customer_profile;
        const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ') || u.display_name || 'Unknown';
        return {
          id: u.id,
          user_id: u.user_id,
          first_name: u.first_name ?? '',
          last_name: u.last_name ?? '',
          display_name: u.display_name ?? '',
          full_name: fullName,
          email: '',
          city: u.city ?? '',
          country: u.country ?? '',
          verification_status: u.verification_status ?? 'unverified',
          is_active: u.is_active ?? true,
          is_suspended: u.is_suspended ?? false,
          created_at: u.created_at,
          customer_code: cp?.customer_code ?? '',
          total_spent: cp?.total_spent ?? 0,
          total_bookings: cp?.total_bookings ?? 0,
          completed_bookings: cp?.completed_bookings ?? 0,
          cancelled_bookings: cp?.cancelled_bookings ?? 0,
          vip_status: cp?.vip_status ?? false,
          customer_status: cp?.status ?? 'active',
          // legacy compat
          jobs_posted: cp?.total_bookings ?? 0,
          dispute_count: 0,
          status: cp?.status ?? (u.is_active ? 'active' : u.is_suspended ? 'suspended' : 'pending'),
          userRoles: [],
        };
      });
    } else {
      customerProfiles = legacyCustomers.map((u: any) => ({
        id: u.id,
        user_id: u.id,
        first_name: u.full_name?.split(' ')[0] ?? '',
        last_name: u.full_name?.split(' ').slice(1).join(' ') ?? '',
        display_name: u.full_name ?? '',
        full_name: u.full_name ?? '',
        email: u.email ?? '',
        city: u.city ?? '',
        country: '',
        verification_status: u.verification_status ?? 'unverified',
        is_active: u.status === 'active',
        is_suspended: u.status === 'suspended',
        created_at: u.created_at,
        total_spent: u.total_spent ?? 0,
        jobs_posted: u.jobs_posted ?? 0,
        dispute_count: u.dispute_count ?? 0,
        status: u.status ?? 'active',
        last_active_at: u.last_active_at ?? '',
        userRoles: [],
      }));
    }

    // Fetch user_roles for all users (using user_id for new schema, id for legacy)
    const allUserIds = [
      ...new Set([
        ...artisanProfiles.map((u) => u.user_id || u.id),
        ...customerProfiles.map((u) => u.user_id || u.id),
      ]),
    ];

    const { data: allUserRoles } =
      allUserIds.length > 0
        ? await supabase
            .from('user_roles')
            .select('id, user_id, role_id, roles!user_roles_role_id_fkey(name)')
            .in('user_id', allUserIds)
        : { data: [] };

    const rolesByUser: Record<string, UserRole[]> = {};
    (allUserRoles ?? []).forEach((r: any) => {
      if (!rolesByUser[r.user_id]) rolesByUser[r.user_id] = [];
      rolesByUser[r.user_id].push({
        id: r.id,
        roleName: r.roles?.name ?? r.role_id?.toString() ?? 'unknown',
        role_id: r.role_id,
      });
    });

    // Enrich with roles
    const enrichArtisans = artisanProfiles.map((u) => ({
      ...u,
      userRoles: rolesByUser[u.user_id || u.id] ?? [],
    }));
    const enrichCustomers = customerProfiles.map((u) => ({
      ...u,
      userRoles: rolesByUser[u.user_id || u.id] ?? [],
    }));

    setArtisans(enrichArtisans);
    setCustomers(enrichCustomers);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredArtisans = useMemo(() => {
    return artisans.filter((a) => {
      const matchSearch =
        !search ||
        a.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        a.email?.toLowerCase().includes(search.toLowerCase()) ||
        (a.specialization ?? a.specialty ?? '').toLowerCase().includes(search.toLowerCase()) ||
        (a.business_name ?? '').toLowerCase().includes(search.toLowerCase());
      const rowStatus = a.artisan_status ?? a.status ?? 'active';
      const matchStatus = statusFilter === 'all' || rowStatus === statusFilter;
      const matchCity = cityFilter === 'all' || a.city === cityFilter;
      return matchSearch && matchStatus && matchCity;
    });
  }, [artisans, search, statusFilter, cityFilter]);

  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
      const matchSearch =
        !search ||
        c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        c.email?.toLowerCase().includes(search.toLowerCase());
      const rowStatus = c.customer_status ?? c.status ?? 'active';
      const matchStatus = statusFilter === 'all' || rowStatus === statusFilter;
      const matchCity = cityFilter === 'all' || c.city === cityFilter;
      return matchSearch && matchStatus && matchCity;
    });
  }, [customers, search, statusFilter, cityFilter]);

  const data = activeTab === 'artisans' ? filteredArtisans : filteredCustomers;
  const totalPages = Math.ceil(data.length / perPage);
  const paginatedData = data.slice((page - 1) * perPage, page * perPage);

  const toggleRow = (id: string) => {
    const next = new Set(selectedRows);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedRows(next);
  };

  const toggleAll = () => {
    if (selectedRows.size === paginatedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginatedData.map((r) => r.id)));
    }
  };

  const handleAction = (action: string, name: string, id: string) => {
    // Determine which table to update based on whether new schema data exists
    const isNewSchema = artisans.some((a) => a.artisan_code) || customers.some((c) => c.customer_code);
    setConfirmModal({
      open: true,
      action,
      name,
      id,
      table: isNewSchema ? 'user_profile' : 'user_profiles',
    });
  };

  const executeAction = async () => {
    setActionLoading(true);
    const supabase = createClient();
    const { action, id, name, table } = confirmModal;

    let error: any = null;

    if (table === 'user_profile') {
      // New schema: update is_active / is_suspended on user_profile
      const updates: Record<string, any> = {
        suspend: { is_suspended: true, is_active: false },
        remove: { is_active: false, is_suspended: true },
        approve: { is_active: true, is_suspended: false },
      };
      const res = await supabase.from('user_profile').update(updates[action]).eq('id', id);
      error = res.error;
    } else {
      // Legacy: update status on user_profiles
      const statusMap: Record<string, string> = {
        suspend: 'suspended',
        remove: 'removed',
        approve: 'active',
      };
      const res = await supabase.from('user_profiles').update({ status: statusMap[action] }).eq('id', id);
      error = res.error;
    }

    if (error) {
      toast.error('Action failed. Please try again.');
    } else {
      const messages: Record<string, string> = {
        suspend: `${name} has been suspended`,
        remove: `${name} has been removed from the platform`,
        approve: `${name} has been approved and activated`,
      };
      toast.success(messages[action] ?? 'Action completed');
      await fetchUsers();
    }

    setActionLoading(false);
    setConfirmModal({ open: false, action: '', name: '', id: '', table: 'user_profiles' });
  };

  const openRoleModal = (user: UserRow) => {
    setNewRole('');
    setRoleModal({ open: true, user });
  };

  const assignRole = async () => {
    if (!roleModal.user || !newRole) return;
    setRoleActionLoading(true);
    const supabase = createClient();

    const { data: roleRow } = await supabase
      .from('roles')
      .select('id')
      .eq('name', newRole)
      .maybeSingle();

    if (!roleRow?.id) {
      toast.error(`Role "${newRole}" not found in roles table`);
      setRoleActionLoading(false);
      return;
    }

    // user_roles.user_id references user_profiles.id (legacy) or auth.users.id
    const userId = roleModal.user.user_id || roleModal.user.id;
    const { error } = await supabase
      .from('user_roles')
      .insert({ user_id: userId, role_id: roleRow.id });

    if (error) {
      toast.error(error.message || 'Failed to assign role');
    } else {
      toast.success(`Role "${newRole.replace(/_/g, ' ')}" assigned to ${roleModal.user.full_name}`);
      setNewRole('');
      await fetchUsers();
    }
    setRoleActionLoading(false);
  };

  const revokeRole = async (roleId: string, roleName: string) => {
    if (!roleModal.user) return;
    setRoleActionLoading(true);
    const supabase = createClient();
    const { error } = await supabase.from('user_roles').delete().eq('id', roleId);
    if (error) {
      toast.error(error.message || 'Failed to revoke role');
    } else {
      toast.success(`Role "${roleName.replace(/_/g, ' ')}" revoked`);
      await fetchUsers();
    }
    setRoleActionLoading(false);
  };

  const allCities = useMemo(() => {
    const citySet = new Set<string>();
    [...artisans, ...customers].forEach((u) => {
      if (u.city) citySet.add(u.city);
    });
    return ['all', ...Array.from(citySet).sort()];
  }, [artisans, customers]);

  const availableRolesToAssign = useMemo(() => {
    if (!roleModal.user) return [];
    const assigned = new Set((roleModal.user.userRoles ?? []).map((r) => r.roleName));
    return [...FRONTEND_ROLES, ...ADMIN_ROLES].filter((r) => !assigned.has(r));
  }, [roleModal.user]);

  if (loading) {
    return (
      <div className="px-6 py-6 max-w-screen-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-8 w-24 bg-muted animate-pulse rounded" />
          <div className="h-8 w-24 bg-muted animate-pulse rounded" />
        </div>
        <div className="card-elevated rounded-xl overflow-hidden">
          <div className="p-4 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`row-skel-${i}`} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-screen-2xl mx-auto">
      {/* Tabs */}
      <div className="flex items-center gap-6 mb-6">
        {[
          { key: 'artisans', label: 'Artisans', count: roleCounts.artisan ?? artisans.length, icon: BadgeCheckIcon },
          { key: 'customers', label: 'Customers', count: roleCounts.customer ?? customers.length, icon: Users },
        ].map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button
              key={`tab-${tab.key}`}
              onClick={() => {
                setActiveTab(tab.key as TabType);
                setPage(1);
                setSelectedRows(new Set());
              }}
              className={`flex items-center gap-2 pb-3 text-sm font-600 border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-accent text-accent' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <TabIcon size={15} />
              {tab.label}
              <span
                className={`px-1.5 py-0.5 text-xs rounded-full ${
                  activeTab === tab.key
                    ? 'bg-accent/10 text-accent' :'bg-muted text-muted-foreground'
                }`}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="card-elevated rounded-xl p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2 flex-1 min-w-48">
            <Search size={14} className="text-muted-foreground shrink-0" />
            <input
              type="text"
              placeholder={
                activeTab === 'artisans' ? 'Search artisans, specialty, business...' : 'Search customers...'
              }
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-full"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter size={14} className="text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
              <option value="removed">Removed</option>
            </select>
          </div>

          <select
            value={cityFilter}
            onChange={(e) => {
              setCityFilter(e.target.value);
              setPage(1);
            }}
            className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground outline-none focus:ring-2 focus:ring-primary/20"
          >
            {allCities.map((c) => (
              <option key={`city-${c}`} value={c}>
                {c === 'all' ? 'All Cities' : c}
              </option>
            ))}
          </select>

          <button className="btn-ghost flex items-center gap-2 border border-border ml-auto">
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedRows.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-primary rounded-xl mb-4 slide-up">
          <span className="text-white text-sm font-600">{selectedRows.size} selected</span>
          <div className="flex-1" />
          <button
            onClick={() => {
              toast.success(`${selectedRows.size} users approved`);
              setSelectedRows(new Set());
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
          >
            <CheckCircle size={13} /> Approve All
          </button>
          <button
            onClick={() => {
              toast.warning(`${selectedRows.size} users suspended`);
              setSelectedRows(new Set());
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-600 bg-amber-500/20 text-amber-200 rounded-lg hover:bg-amber-500/30 transition-colors"
          >
            <Ban size={13} /> Suspend All
          </button>
          <button
            onClick={() => setSelectedRows(new Set())}
            className="text-white/60 hover:text-white text-xs"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="card-elevated rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded accent-primary"
                  />
                </th>
                {activeTab === 'artisans' ? (
                  <>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">City</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Specialization</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Rating</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Jobs Done</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Hourly Rate</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Roles</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Verification</th>
                    <th className="w-24 px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide text-center">Actions</th>
                  </>
                ) : (
                  <>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Name</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">City</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Bookings</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Total Spent</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">VIP</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Roles</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Joined</th>
                    <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide">Status</th>
                    <th className="w-24 px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide text-center">Actions</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-16 text-center">
                    <Users size={32} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm font-600 text-foreground mb-1">No {activeTab} found</p>
                    <p className="text-xs text-muted-foreground">Try adjusting your search or filter criteria.</p>
                  </td>
                </tr>
              ) : (
                paginatedData.map((row) => {
                  const rowStatus = activeTab === 'artisans' ? (row.artisan_status ?? row.status ??'active')
                    : (row.customer_status ?? row.status ?? 'active');
                  return (
                    <tr key={row.id} className="table-row-hover transition-colors group">
                      <td className="px-4 py-3.5">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(row.id)}
                          onChange={() => toggleRow(row.id)}
                          className="w-4 h-4 rounded accent-primary"
                        />
                      </td>

                      {activeTab === 'artisans' ? (
                        <>
                          <td className="px-4 py-3.5">
                            <div>
                              <p className="text-sm font-600 text-foreground">{row.full_name}</p>
                              {row.artisan_code && (
                                <p className="text-xs text-muted-foreground font-mono">{row.artisan_code}</p>
                              )}
                              {row.business_name && (
                                <p className="text-xs text-muted-foreground">{row.business_name}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin size={11} />
                              {row.city || '—'}
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="text-xs font-500 bg-primary/8 text-primary px-2 py-0.5 rounded-full">
                              {row.specialization || row.specialty || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3.5">
                            <TrustStars score={row.average_rating ?? row.trust_score ?? 0} />
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Briefcase size={11} />
                              <span className="font-tabular font-600 text-foreground">
                                {row.completed_jobs ?? row.jobs_completed ?? 0}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="text-xs font-600 font-tabular text-foreground">
                              {row.hourly_rate ? `OMR ${row.hourly_rate}/hr` : row.total_revenue ? `OMR ${(row.total_revenue ?? 0).toLocaleString()}` : '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3.5">
                            <RoleBadges roles={row.userRoles} />
                          </td>
                          <td className="px-4 py-3.5">
                            <StatusBadge status={rowStatus as any} size="sm" />
                          </td>
                          <td className="px-4 py-3.5">
                            <StatusBadge status={row.verification_status as any} size="sm" />
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3.5">
                            <div>
                              <p className="text-sm font-600 text-foreground">{row.full_name}</p>
                              {row.customer_code && (
                                <p className="text-xs text-muted-foreground font-mono">{row.customer_code}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin size={11} />
                              {row.city || '—'}
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="text-xs font-600 font-tabular text-foreground">
                              {row.total_bookings ?? row.jobs_posted ?? 0}
                            </span>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="text-xs font-600 font-tabular text-foreground">
                              OMR {(row.total_spent ?? 0).toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-3.5">
                            {row.vip_status ? (
                              <span className="text-xs font-600 text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">VIP</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3.5">
                            <RoleBadges roles={row.userRoles} />
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="text-xs text-muted-foreground font-tabular">{formatDate(row.created_at)}</span>
                          </td>
                          <td className="px-4 py-3.5">
                            <StatusBadge status={rowStatus as any} size="sm" />
                          </td>
                        </>
                      )}

                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            title="Manage roles"
                            onClick={() => openRoleModal(row)}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
                          >
                            <Tag size={14} />
                          </button>
                          <button
                            title="Suspend user"
                            onClick={() => handleAction('suspend', row.full_name, row.id)}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          >
                            <Ban size={14} />
                          </button>
                          <button
                            title="Remove user"
                            onClick={() => handleAction('remove', row.full_name, row.id)}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Showing {Math.min((page - 1) * perPage + 1, data.length)}–{Math.min(page * perPage, data.length)} of{' '}
            <span className="font-600 text-foreground">{data.length}</span> {activeTab}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={`page-${i + 1}`}
                onClick={() => setPage(i + 1)}
                className={`w-8 h-8 text-xs rounded-lg font-600 transition-colors ${
                  page === i + 1
                    ? 'bg-primary text-white' :'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || totalPages === 0}
              className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      <Modal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, action: '', name: '', id: '', table: 'user_profiles' })}
        title={
          confirmModal.action === 'suspend' ?'Suspend User'
            : confirmModal.action === 'remove' ?'Remove User' :'Approve User'
        }
        size="sm"
      >
        <p className="text-sm text-muted-foreground mb-6">
          {confirmModal.action === 'suspend'
            ? `Suspend ${confirmModal.name}? They will lose access to the platform immediately.`
            : confirmModal.action === 'remove'
            ? `Permanently remove ${confirmModal.name}? This action cannot be undone.`
            : `Approve and activate ${confirmModal.name}?`}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setConfirmModal({ open: false, action: '', name: '', id: '', table: 'user_profiles' })}
            className="btn-ghost border border-border"
          >
            Cancel
          </button>
          <button
            onClick={executeAction}
            disabled={actionLoading}
            className={`${confirmModal.action === 'remove' ? 'btn-danger' : 'btn-primary'} flex items-center gap-2 disabled:opacity-70`}
          >
            {actionLoading && <Loader2 size={14} className="animate-spin" />}
            {confirmModal.action === 'suspend' ?'Suspend'
              : confirmModal.action === 'remove' ?'Remove' :'Approve'}
          </button>
        </div>
      </Modal>

      {/* Role management modal */}
      <Modal
        open={roleModal.open}
        onClose={() => setRoleModal({ open: false, user: null })}
        title={`Manage Roles — ${roleModal.user?.full_name ?? ''}`}
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">
              Assigned Roles
            </p>
            {(roleModal.user?.userRoles ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No roles assigned yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(roleModal.user?.userRoles ?? []).map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/10 text-accent text-xs font-600"
                  >
                    {r.roleName.replace(/_/g, ' ')}
                    <button
                      onClick={() => revokeRole(r.id, r.roleName)}
                      disabled={roleActionLoading}
                      className="hover:text-red-500 transition-colors"
                      title="Revoke role"
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">
              Assign New Role
            </p>
            <div className="flex gap-2">
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="flex-1 text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Select a role...</option>
                <optgroup label="Frontend Roles">
                  {FRONTEND_ROLES.filter(
                    (r) => !(roleModal.user?.userRoles ?? []).some((ur) => ur.roleName === r)
                  ).map((r) => (
                    <option key={r} value={r}>
                      {r.replace(/_/g, ' ')}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Admin Roles">
                  {ADMIN_ROLES.filter(
                    (r) => !(roleModal.user?.userRoles ?? []).some((ur) => ur.roleName === r)
                  ).map((r) => (
                    <option key={r} value={r}>
                      {r.replace(/_/g, ' ')}
                    </option>
                  ))}
                </optgroup>
              </select>
              <button
                onClick={assignRole}
                disabled={!newRole || roleActionLoading}
                className="btn-primary flex items-center gap-1.5 disabled:opacity-60"
              >
                {roleActionLoading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                Assign
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}